import { parseStartParam } from './verify-init-data'

// Subset of WebApp.initDataUnsafe we care about. `chat` is only present for
// attachment-menu / join-request launches; direct links give chat_instance and
// chat_type without an id.
export type LaunchInitData = {
  start_param?: string
  chat?: { id?: number; type?: string }
  chat_type?: string
  chat_instance?: string
}

export type LaunchDeps = {
  // Resolves a Telegram chat id to our group id (GET /api/groups/by-chat), or null.
  groupByChat?: (chatId: number) => Promise<string | null>
}

export function routeForStartParam(startParam: string | undefined | null): string | null {
  const sp = parseStartParam(startParam)
  switch (sp.kind) {
    case 'quote':
      return `/g/${sp.groupId}/q/${sp.localId}`
    case 'group':
      return `/g/${sp.groupId}`
    case 'game':
      return `/g/${sp.groupId}/game`
    case 'top':
      return `/g/${sp.groupId}/top`
    case 'settings':
      return sp.groupId ? `/g/${sp.groupId}/settings` : '/settings'
    default:
      return null
  }
}

const GROUP_TYPES = new Set(['group', 'supergroup'])

export async function resolveLaunchRoute(
  initDataUnsafe: LaunchInitData | undefined,
  deps: LaunchDeps = {},
): Promise<string> {
  const byParam = routeForStartParam(initDataUnsafe?.start_param)
  if (byParam) return byParam

  const chatId = initDataUnsafe?.chat?.id
  const chatType = initDataUnsafe?.chat?.type ?? initDataUnsafe?.chat_type
  if (typeof chatId === 'number' && (!chatType || GROUP_TYPES.has(chatType)) && deps.groupByChat) {
    try {
      const id = await deps.groupByChat(chatId)
      if (id) return `/g/${id}`
    } catch {
      // fall through to the picker
    }
  }
  return '/groups'
}

// Client helper for the by-chat endpoint.
export function fetchGroupByChat(authHeader: string): (chatId: number) => Promise<string | null> {
  return async (chatId) => {
    const res = await fetch(`/api/groups/by-chat?chatId=${chatId}`, { headers: { authorization: authHeader } })
    if (!res.ok) return null
    const j = (await res.json()) as { id?: string }
    return j.id ?? null
  }
}
